import { EquipmentExtType } from '../contexts/types';
import GoogleAPI from '../utils/GoogleAPI';
import { isValidYearMonth, padYearMonth } from '../utils/utils';
import i18n from '../utils/i18n';

const T_PREFIX = 'features.InvestmentScreening.PhasedCapitalInvestment.spreadsheet';

/**
 * 段階的設備投資データ変換結果
 */
export interface PhasedCapitalInvestmentConversionResult {
  data: EquipmentExtType[];
  errors: string[];
}

/**
 * 段階的設備投資データをSpreadSheetデータに変換
 * @param equipmentAmounts 設備リスト
 * @returns SpreadSheet用の2次元配列
 */
export const phasedCapitalInvestmentToSpreadSheetData = (equipmentAmounts: EquipmentExtType[]): any[][] => {
  // ヒント行（1行目）
  const hint = [i18n.t(`${T_PREFIX}.hint`), "", "", "", "", "", "", ""];

  // ヘッダー行（2行目）
  const header = [
    i18n.t(`${T_PREFIX}.header.equipmentName`),
    i18n.t(`${T_PREFIX}.header.unitPrice`),
    i18n.t(`${T_PREFIX}.header.quantity`),
    i18n.t(`${T_PREFIX}.header.amount`),
    i18n.t(`${T_PREFIX}.header.acceptanceMonthBefore`),
    i18n.t(`${T_PREFIX}.header.acceptanceMonthAfter`),
    i18n.t(`${T_PREFIX}.header.depreciationStartMonthBefore`),
    i18n.t(`${T_PREFIX}.header.depreciationStartMonthAfter`),
  ];

  // データ行（3行目から）
  const dataRows = equipmentAmounts.map((e, index) => {
    const row = index + 3; // ヒント1行 + ヘッダー1行 なので、データは3行目から
    return [
      `${e.equipmentName}（${e.equipmentId}）`,
      e.unitPrice ?? 0,
      e.changeInQuantity ?? 0,
      `=B${row}*C${row}`, // 金額 = 単価 × 数量（数式）
      padYearMonth(e.acceptanceMonth),
      padYearMonth(e.changedAcceptanceMonth ?? e.acceptanceMonth),
      padYearMonth(e.depreciationStartMonth),
      padYearMonth(e.changedDepreciationStartMonth ?? e.depreciationStartMonth),
    ];
  });

  return [hint, header, ...dataRows];
};

/**
 * SpreadSheetデータを段階的設備投資データに変換
 * データ連携で連携する項目: 検収月(変更後)、償却開始月(変更後)
 * @param values SpreadSheetから取得した2次元配列（ヒント行・ヘッダー行を含まない、A3:H から取得）
 * @param existingData 既存の設備リスト
 * @returns 変換結果
 */
export const spreadSheetDataToPhasedCapitalInvestment = (
  values: any[][],
  existingData: EquipmentExtType[]
): PhasedCapitalInvestmentConversionResult => {
  if (!values || values.length === 0) {
    return { data: [], errors: [] };
  }

  // 既存データのコピーを作成
  const updatedData = existingData.map(e => ({ ...e }));
  const errors: string[] = [];

  for (let i = 0; i < values.length; i++) {
    const row = values[i];
    if (!row || row.length === 0) continue;

    // A列: 設備名（設備ID）から設備IDを抽出（特定できない場合はエラー）
    const equipmentNameWithId = row[0]?.toString() ?? "";
    const idMatch = equipmentNameWithId.match(/.*（(.+)）$/);
    const equipmentId = idMatch ? idMatch[1] : null;
    if (!equipmentId) {
      if (equipmentNameWithId) {
        errors.push(i18n.t(`${T_PREFIX}.message.equipmentNotIdentified`, { row: i + 1, value: equipmentNameWithId }));
      }
      continue;
    }

    // 既存データから該当する設備を検索（見つからない場合はエラー）
    const targetIndex = updatedData.findIndex(e => e.equipmentId === equipmentId);
    if (targetIndex === -1) {
      errors.push(i18n.t(`${T_PREFIX}.message.equipmentNotFound`, { row: i + 1, equipmentId }));
      continue;
    }

    const equipmentName = updatedData[targetIndex].equipmentName;

    // F列(index 5): 検収月(変更後) - 不正な日付は空白
    const changedAcceptanceRaw = row[5]?.toString().trim() ?? "";
    const changedAcceptance = isValidYearMonth(row[5]);
    if (changedAcceptanceRaw && !changedAcceptance) {
      errors.push(i18n.t(`${T_PREFIX}.message.changedAcceptanceInvalid`, { equipmentName, value: changedAcceptanceRaw }));
    }
    updatedData[targetIndex].changedAcceptanceMonth = changedAcceptance || undefined;

    // H列(index 7): 償却開始月(変更後) - 不正な日付は空白
    const changedDepreciationRaw = row[7]?.toString().trim() ?? "";
    const changedDepreciation = isValidYearMonth(row[7]);
    if (changedDepreciationRaw && !changedDepreciation) {
      errors.push(i18n.t(`${T_PREFIX}.message.changedDepreciationInvalid`, { equipmentName, value: changedDepreciationRaw }));
    }
    updatedData[targetIndex].changedDepreciationStartMonth = changedDepreciation || undefined;
  }

  return { data: updatedData, errors };
};

/**
 * 段階的設備投資シートの入力規則・背景色を設定
 * データ書き込み後に呼び出す共通処理
 * @param googleAPI GoogleAPIインスタンス
 * @param spreadsheetId SpreadSheet ID
 * @param sheetId シートID
 * @param equipmentAmounts 設備リスト
 */
export const setupPhasedCapitalInvestmentSheet = async (
  googleAPI: GoogleAPI,
  spreadsheetId: string,
  sheetId: number,
  equipmentAmounts: EquipmentExtType[]
): Promise<void> => {
  const dataRowCount = equipmentAmounts.length;
  if (dataRowCount === 0) return;

  // 入力規則の設定（設備名プルダウン）
  await googleAPI.spreadSheetSetDataValidation(spreadsheetId, [
    {
      sheetId,
      startRow: 3,
      endRow: 2 + dataRowCount,
      startCol: 1,
      endCol: 1,
      values: equipmentAmounts.map(e => `${e.equipmentName}（${e.equipmentId}）`),
      strict: true,
      showDropdown: true,
    },
  ]);

  // 検収月・償却開始月の表示形式をyyyy-mmに設定
  const dateFormat = { numberFormat: { type: "DATE" as const, pattern: "yyyy-mm" } };
  await googleAPI.spreadSheetSetCellFormat(spreadsheetId, [
    { sheetId, startRow: 3, endRow: 2 + dataRowCount, startCol: 5, endCol: 6, format: dateFormat },  // E-F列: 検収月(変更前/後)
    { sheetId, startRow: 3, endRow: 2 + dataRowCount, startCol: 7, endCol: 8, format: dateFormat },  // G-H列: 償却開始月(変更前/後)
  ]);

  // 編集対象外の列に薄い灰色の背景色を設定
  // A:設備名(1), B:単価(2), C:数量(3), D:金額(4), E:検収月(変更前)(5), G:償却開始月(変更前)(7)
  const readOnlyBgColor = { red: 0.93, green: 0.93, blue: 0.93 };
  await googleAPI.spreadSheetSetCellFormat(spreadsheetId, [
    { sheetId, startRow: 3, endRow: 2 + dataRowCount, startCol: 1, endCol: 5, format: { backgroundColor: readOnlyBgColor } },
    { sheetId, startRow: 3, endRow: 2 + dataRowCount, startCol: 7, endCol: 7, format: { backgroundColor: readOnlyBgColor } },
  ]);
};
